<template>
  <div class="group-setup">
    <v-progress-linear
      indeterminate
      color="blue"
      v-if="loading"
    ></v-progress-linear>
    <v-progress-linear
      indeterminate
      color="blue"
      v-if="loadingData && !loading"
    ></v-progress-linear>
    <v-layout row v-if="errors">
      <v-flex>
        <Alert @dismissed="onDismissed" :text="errors"></Alert>
      </v-flex>
    </v-layout>
    <v-form v-if="!loading">
      <v-row v-if="customers">
        <v-col cols="12" md="12" sm="12">
          <v-card
            class="pa-5"
            :style="{ border: '1px solid var(--primary-boarder-color)' }"
          >
            <HeaderDividerBlock
              headerText="Project Stakeholders"
            ></HeaderDividerBlock>
            <v-row>
              <v-col cols="6" md="4" sm="12">
                <v-autocomplete
                  v-model="form.customer"
                  :items="formattedCustomers"
                  item-value="uuid"
                  outlined
                  dense
                  clearable
                  :search-input.sync="search"
                  @update:search-input="searchChange"
                  :error-messages="this.fieldErrors.customer_id"
                  :label="customerLabel"
                >
                </v-autocomplete>
              </v-col>
              <v-col cols="6" md="4" sm="12">
                <v-autocomplete
                  v-model="form.manager"
                  :items="managers"
                  :item-text="formatMemberText"
                  :item-value="'uuid'"
                  label="Project Manager*"
                  dense
                  outlined
                  clearable
                  :search-input.sync="searchManagerUser"
                  @update:search-input="searchManagerUserChange"
                  :error-messages="this.fieldErrors.manager_id"
                ></v-autocomplete>
              </v-col>
              <v-col cols="6" md="4" sm="12">
                <v-autocomplete
                  v-model="form.member"
                  :items="members"
                  :item-text="formatMemberText"
                  :item-value="'uuid'"
                  label="Members"
                  dense
                  outlined
                  multiple
                  clearable
                  :search-input.sync="searchUser"
                  @update:search-input="searchUserChange"
                  :error-messages="this.fieldErrors.member_id"
                ></v-autocomplete>
              </v-col>
            </v-row>
          </v-card>
          <v-card
            class="pa-5 my-5"
            :style="{ border: '1px solid var(--primary-boarder-color)' }"
          >
            <HeaderDividerBlock
              headerText="Project Details"
            ></HeaderDividerBlock>
            <v-row>
              <v-col cols="6" md="6" sm="12">
                <v-text-field
                  outlined
                  dense
                  v-model="form.project_name"
                  label="Project Name*"
                  required
                  :error-messages="this.fieldErrors.project_name"
                ></v-text-field>
              </v-col>
              <v-col cols="6" md="6" sm="12">
                <v-textarea
                  rows="1"
                  outlined
                  dense
                  v-model="form.project_description"
                  label="Project Description*"
                  :error-messages="this.fieldErrors.project_description"
                >
                </v-textarea>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6" md="3" sm="12">
                <v-autocomplete
                  v-model="form.project_type"
                  :items="projectTypes"
                  :item-text="'name'"
                  :item-value="'uuid'"
                  label="Project Type*"
                  dense
                  outlined
                  @change="typeChange()"
                  clearable
                  :error-messages="this.fieldErrors.project_type"
                ></v-autocomplete>
              </v-col>
              <v-col cols="6" md="3" sm="12">
                <v-autocomplete
                  v-model="form.priority"
                  :items="priority"
                  label="Project Priority"
                  dense
                  outlined
                  @change="typeChange()"
                  clearable
                  :error-messages="this.fieldErrors.priority"
                ></v-autocomplete>
              </v-col>
              <v-col cols="6" md="3" sm="12">
                <DatePicker
                  :label="'Start Date*'"
                  v-model="form.start_date"
                  :selectValue="form.start_date"
                  offset-y
                  @changeInput="form.start_date = $event"
                  :error="this.fieldErrors.start_date"
                  :maxDate="maxStartDate"
                >
                </DatePicker>
              </v-col>
              <v-col cols="6" md="3" sm="12">
                <DatePicker
                  :label="'Due Date*'"
                  v-model="form.due_date"
                  :selectValue="form.due_date"
                  offset-y
                  @changeInput="form.due_date = $event"
                  :error="this.fieldErrors.due_date"
                  :minDate="minDueDate"
                >
                </DatePicker>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6" md="3" sm="12">
                <v-autocomplete
                  v-model="form.project_branch"
                  :items="projectBranches"
                  :item-text="'name'"
                  :item-value="'uuid'"
                  label="Project Branch*"
                  dense
                  outlined
                  @change="typeChange()"
                  clearable
                  :error-messages="this.fieldErrors.project_branch"
                ></v-autocomplete>
              </v-col>
              <v-col cols="6" md="6" sm="12">
                <v-row>
                  <v-col cols="8">
                    <div @click="handleTextareaClick">
                      <v-textarea
                        outlined
                        dense
                        rows="1"
                        v-model="form.project_location"
                        label="Project Location"
                        :error-messages="this.fieldErrors.project_location"
                      ></v-textarea>
                    </div>
                  </v-col>
                  <v-col cols="4" class="ma-a pa-0">
                    <v-checkbox
                      v-model="customerLocation"
                      @click="setCustomerLocation()"
                      label="Customer Location"
                    ></v-checkbox>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="6" md="3" sm="12">
                <v-textarea
                  outlined
                  dense
                  rows="1"
                  v-model="form.project_location_link"
                  label="Location Map Link"
                  :error-messages="this.fieldErrors.location_map_url"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-card>
          <v-card
            class="pa-5"
            :style="{ border: '1px solid var(--primary-boarder-color)' }"
          >
            <HeaderDividerBlock
              headerText="Project Parameters"
            ></HeaderDividerBlock>

            <v-row>
              <v-col cols="6" md="4" sm="12">
                <v-text-field
                  outlined
                  dense
                  v-model="form.gross_floor_area"
                  label="Gross Floor Area (m²)"
                  :error-messages="this.fieldErrors.gross_floor_area"
                ></v-text-field>
              </v-col>
              <v-col cols="6" md="4" sm="12">
                <v-text-field
                  outlined
                  dense
                  v-model="form.build_up_area"
                  label="Built Up Area (m²)"
                  :error-messages="this.fieldErrors.build_up_area"
                ></v-text-field>
              </v-col>
              <v-col cols="6" md="4" sm="12">
                <v-text-field
                  outlined
                  dense
                  v-model="form.budget"
                  :label="`Budget (${currencyCode})*`"
                  required
                  :error-messages="this.fieldErrors.budget"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col
                v-for="field in dynamicFields"
                :key="field.uuid"
                cols="6"
                md="6"
                sm="12"
              >
                <component
                  :is="getFieldComponent(field.input_type)"
                  :field="field"
                  @custom-update="form[field.key] = $event"
                ></component>
              </v-col>
            </v-row>
            <v-divider></v-divider>
            <v-card-actions v-if="!cpRole">
              <v-row>
                <v-col cols="12" md="9" xs="6"></v-col>
                <v-col cols="12" md="3" xs="6">
                  <v-btn
                    large
                    color="#00B3FF"
                    dark
                    depressed
                    block
                    @click="submit()"
                    >Create New Project</v-btn
                  >
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col>
          <v-alert type="info" text="" variant="tonal" color="red"
            >Can't create job. Please add a client before creating the
            job.</v-alert
          >
        </v-col>
      </v-row>
    </v-form>

    <Snackbars
      :text="succesMsg"
      :snackbar="snackbar"
      :color="'success'"
      @classSnackbar="clossSnackbar"
    />
  </div>
</template>

<script>
import DatePicker from "@/components/shared/DatePicker";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");
import Alert from "@/components/shared/Alert";
import Snackbars from "@/components/shared/Snackbars";
import DynamicTextField from "@/components/shared/DynamicTextField";
import DynamicAutocomplete from "@/components/shared/DynamicAutocomplete";
import DynamicDatepicker from "@/components/shared/DynamicDatepicker";
import DynamicTextarea from "@/components/shared/DynamicTextarea";
import DynamicMultiselect from "@/components/shared/DynamicMultiselect";
import { ProjectService } from "../../services/project-service.js";
import { CustomerService } from "@/services/customer-service.js";
import { checkError } from "@/config/errors.js";
import HeaderDividerBlock from "@/components/shared/HeaderDividerBlock";
import { EmployerService } from "@/services/employer-service.js";
import debounce from "lodash.debounce";
const { mapGetters: mapSettingsGetters } = createNamespacedHelpers("settings");

export default {
  name: "CreateProject",

  components: {
    Alert,
    Snackbars,
    DatePicker,
    HeaderDividerBlock,
    DynamicTextField,
    DynamicAutocomplete,
    DynamicDatepicker,
    DynamicMultiselect,
    DynamicTextarea,
  },
  props: ["uuid"],
  data: () => ({
    loading: false,
    loadingData: false,
    errors: null,
    page: 1,
    customerLocation: false,
    customer_address: "",
    customer_city: "",
    dynamicFields: [],
    projectTypes: [],
    projectBranches: [],
    customer_state: "",
    customer_country: "",
    search: "",
    searchUser: " ",
    searchManagerUser: " ",
    snackbar: false,
    succesMsg: "",
    form: {
      project_name: "",
      priority: "",
      customer: null,
      start_date: "",
      due_date: "",
    },
    role: localStorage.getItem("role") || null,
    customers: [],
    members: [],
    managers: [],
    cpRole: false,

    priority: ["LOW", "MEDIUM", "HIGH"],
    fieldErrors: [],
    minDueDate: "",
    maxStartDate: "",
  }),

  mounted() {
    this.getCustomersList();
    this.getMembersList();
    this.getDynamicFields();
    this.getProjectTypes();
    this.getProjectBranches();
  },

  computed: {
    ...mapGetters({
      member: "profile",
    }),
    ...mapSettingsGetters({
      currencyCode: "currencyCode",
    }),

    formattedCustomers() {
      return this.customers.map((customer) => ({
        ...customer,
        text: `${customer.code} - ${customer.full_name} - ${customer.company_name}`,
      }));
    },
    customerLabel() {
      return this.form.customer ? "Customer" : "Search Customer*";
    },
  },
  watch: {
    search() {
      this.page = 1;
      if (this.search !== null) {
        const parts = this.search.split(" -");
        this.search = parts[0];
        this.getCustomersList();
      }
    },
    searchUser() {
      this.page = 1;
      this.getMembersList();
    },
    searchManagerUser() {
      this.page = 1;
      this.getMembersList();
    },
    "form.start_date"(newVal) {
      this.minDueDate = newVal;
    },
    "form.due_date"(newVal) {
      this.maxStartDate = newVal;
    },
  },

  methods: {
    submit() {
      this.errors = null;
      this.loading = true;
      let fd = new FormData();

      fd.append("project_name", this.form.project_name || "");
      fd.append("start_date", this.form.start_date || "");
      fd.append("due_date", this.form.due_date || "");
      fd.append("priority", this.form.priority || "");
      fd.append("project_description", this.form.project_description || "");
      fd.append("project_location", this.form.project_location || "");
      fd.append("location_map_url", this.form.project_location_link || "");
      fd.append("manager_id", this.form.manager || "");
      fd.append("member_id", this.form.member || "");
      fd.append("customer_id", this.form.customer || "");
      fd.append("gross_floor_area", this.form.gross_floor_area || "");
      fd.append("build_up_area", this.form.build_up_area || "");
      fd.append("project_type", this.form.project_type || "");
      fd.append("project_branch", this.form.project_branch || "");
      fd.append("budget", this.form.budget || "");
      fd.append("is_customer_location", this.customerLocation ? 1 : 0);

      this.dynamicFields.forEach((field) => {
        let value = this.form[field.key];
        if (field.input_type === "Multi_Dropdown" && Array.isArray(value)) {
          value = value.join(",");
        }

        fd.append(field.key, value || "");
      });

      ProjectService.createProject(fd)
        .then((response) => {
          this.loading = false;
          if (response.status == 200) {
            this.succesMsg = "Success";
            this.snackbar = true;
            this.reset();
            const uuid = response.data[0].uuid;
            this.$router.push(`/project/info/${uuid}`);
          }
        })
        .catch((error) => {
          this.loading = false;
          let data = error.response.data;
          let errorOutput = checkError.errorFormatting(data);
          this.fieldErrors = data.errors;
          this.errors = errorOutput;
        });
    },

    getCustomersList: debounce(function() {
      this.loadingData = true;
      CustomerService.getCustomerList(this.search, this.page)
        .then((response) => {
          this.loadingData = false;
          if (response.data.data) {
            this.customers = response.data.data;
            this.total = response.data.meta.last_page || 0;
            this.totalCustomer = response.data.meta.total || 0;
          }
        })
        .catch((error) => {
          this.loading = false;
          this.customers = [];
          let data = error.response.data;
          let errorOutput = checkError.errorFormatting(data);
          this.errors = errorOutput;
        });
    }, 1000),

    setCustomerLocation() {
      if (this.customerLocation && this.form.customer !== null) {
        this.getCustomerAddress();
      } else {
        this.form.project_location = null;
      }
    },
    handleTextareaClick() {
      this.customerLocation = false;
    },

    getFieldComponent(inputType) {
      const components = {
        Input: "DynamicTextField",
        Dropdown: "DynamicAutocomplete",
        Date_Picker: "DynamicDatepicker",
        Multi_Dropdown: "DynamicMultiselect",
        Text_Area: "DynamicTextarea",
      };
      return components[inputType] || "DynamicTextField";
    },

    getCustomerAddress() {
      this.loadingData = true;
      CustomerService.getProjectCustomerAddress(this.form.customer)
        .then((response) => {
          this.loadingData = false;
          if (response.data.data) {
            const customerData = response.data.data;
            this.customer_address = customerData.address1;
            this.customer_city = customerData.city;
            this.customer_state = customerData.state;
            this.customer_country = customerData.country;
            this.form.project_location =
              this.customer_address +
              ", " +
              this.customer_city +
              ", " +
              this.customer_state +
              ", " +
              this.customer_country;
          }
        })
        .catch((error) => {
          this.loading = false;
          let data = error.response.data;
          let errorOutput = checkError.errorFormatting(data);
          this.errors = errorOutput;
        });
    },

    getMembersList() {
      this.loadingData = true;
      EmployerService.getEmployerList(
        this.searchUser ?? this.searchUser ?? "",
        this.page
      )
        .then((response) => {
          this.loadingData = false;
          if (response.data.data) {
            this.managers = response.data.data;
            this.members = response.data.data;
            this.total = response.data.meta.last_page || 0;
            this.totalCustomer = response.data.meta.total || 0;
          }
        })
        .catch((error) => {
          this.loadingData = false;
          let data = error.response.data;
          let errorOutput = checkError.errorFormatting(data);
          this.errors = errorOutput;
        });
    },

    getDynamicFields() {
      this.loading = true;
      ProjectService.getDynamicProjectFields()
        .then((response) => {
          this.loading = false;
          if (response.data.data) {
            this.dynamicFields = response.data.data;
          }
        })
        .catch((error) => {
          this.loading = false;
          let data = error.response.data;
          let errorOutput = checkError.errorFormatting(data);
          this.errors = errorOutput;
        });
    },

    getProjectTypes() {
      this.loading = true;
      ProjectService.getProjectTypes()
        .then((response) => {
          this.loading = false;
          if (response.data.data) {
            this.projectTypes = response.data.data;
          }
        })
        .catch((error) => {
          this.loading = false;
          let data = error.response.data;
          let errorOutput = checkError.errorFormatting(data);
          this.errors = errorOutput;
        });
    },

    getProjectBranches() {
      this.loading = true;
      ProjectService.getProjectBranches()
        .then((response) => {
          this.loading = false;
          if (response.data.data) {
            this.projectBranches = response.data.data;
          }
        })
        .catch((error) => {
          this.loading = false;
          let data = error.response.data;
          let errorOutput = checkError.errorFormatting(data);
          this.errors = errorOutput;
        });
    },

    onDismissed() {
      this.errors = null;
    },
    clossSnackbar() {
      this.snackbar = false;
    },
    reset() {
      this.form = [];
    },
    searchChange(searchText) {
      if (searchText !== "") {
        this.search = searchText;
      } else {
        this.search = null;
      }
    },
    searchUserChange(searchText) {
      this.searchUser = searchText;
    },
    searchManagerUserChange(searchText) {
      this.searchManagerUser = searchText;
    },
    formatMemberText(member) {
      if (member && member.speciality) {
        return `${member.full_name} - ${member.speciality.name}`;
      } else {
        return member.full_name;
      }
    },
  },
};
</script>
